import React, { useEffect, useRef, useState, useCallback } from 'react';
import { SceneManager } from '../three/SceneManager.js';
import UIOverlay         from './UIOverlay.jsx';
import NationsPanel      from './NationsPanel.jsx';
import { useWebSocket }  from '../hooks/useWebSocket.js';
import OnboardingPopup   from './OnboardingPopup';
import ShareCard         from './ShareCard';
import { COUNTRIES }     from '../data/countries.js'; // Added this lookup backup

function getSessionId() {
  let id = localStorage.getItem('rh_session');
  if (!id) {
    id = typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : Math.random().toString(36).slice(2) + Date.now().toString(36);
    localStorage.setItem('rh_session', id);
  }
  return id;
}

export default function RadiantHorizon() {
  const canvasRef   = useRef(null);
  const sceneRef    = useRef(null);
  const sessionId   = useRef(getSessionId());

  const { stats, config, connected, contribute } = useWebSocket();
  const [contributed,   setContributed]   = useState(false); 
  const [popup,         setPopup]         = useState(null);
  const [nationsVisible, setNationsVisible] = useState(false);
  const [shareData,      setShareData]      = useState(null);

  useEffect(() => {
    const mgr = new SceneManager(canvasRef.current);
    mgr.init();
    sceneRef.current = mgr;
    return () => mgr.dispose();
  }, []);

  useEffect(() => {
    sceneRef.current?.updateStats(stats, stats.stage, config?.atmosphere);
  }, [stats, config]);

  const showPopup = (msg, error = false) => {
    setPopup({ msg, error });
    setTimeout(() => setPopup(null), 4000);
  };

  const handleContribute = useCallback(async (nationality) => {
    // 1. Visual feedback for the 3D scene
    sceneRef.current?.addFlagForFirstContribution(nationality);
    
    const result = await contribute(nationality, sessionId.current);
    
    if (result.success) {
      setContributed(true);
      localStorage.setItem('rh_contributed', Date.now().toString());
      showPopup(`+1 Grain added from ${nationality}`);

      const backupIso = COUNTRIES[nationality];
      
      // The backend returns the full state including the field "total"
      // We use result.total as the primary truth.
      const realGlobalTotal = result.total || stats.total || 0;

      setShareData({
        // Uses the database ID if provided, otherwise our custom "Nation-Time" ID
        grainId: result.id || `${backupIso.toLowerCase()}-${Date.now().toString().slice(-4)}`,
        nationality: nationality,
        grainNumber: realGlobalTotal, 
        iso: result.iso || backupIso 
      });

    } else if (result.error && result.error.includes('Already')) {
      setContributed(true);
      localStorage.setItem('rh_contributed', Date.now().toString());
      showPopup('Grain already added today! Come back tomorrow.', true);
    } else {
      showPopup('Could not reach the shield — please try again.', true);
    }
  }, [contribute, stats.total]); // Added stats.totalGrains to dependency array
  return (
    <div className="rh-root">
      <OnboardingPopup />
      <canvas ref={canvasRef} className="rh-canvas" />

      <UIOverlay
        stats={stats}
        stage={stats.stage}
        onContribute={handleContribute}
        connected={connected}
        contributed={contributed}
        popup={popup}
        onViewNations={() => setNationsVisible(v => !v)}
      />

      <NationsPanel
        stats={stats}
        visible={nationsVisible}
        onClose={() => setNationsVisible(false)}
      />

      <ShareCard 
        data={shareData} 
        onClose={() => setShareData(null)} 
      />
    </div>
  );
}